import type translations from "./translations";

export type Locale = keyof typeof translations;
type BaseLocale = (typeof translations)["en"];

type TranslationLeaf = string | ((...args: never[]) => string);

/** Build a union of dot-separated keys */
type DotPath<T, Prefix extends string = ""> = {
  [K in keyof T & string]: T[K] extends TranslationLeaf
    ? `${Prefix}${K}`
    : DotPath<T[K], `${Prefix}${K}.`>;
}[keyof T & string];

/** Resolve the type at a given dot-path */
type DotValue<T, Path extends string> = Path extends `${infer Head}.${infer Tail}`
  ? Head extends keyof T
    ? DotValue<T[Head], Tail>
    : never
  : Path extends keyof T
    ? T[Path]
    : never;

type AllKeys = DotPath<BaseLocale>;

export type TranslationKey = AllKeys;

/** Keys that map to plain strings */
export type StaticKey = {
  [K in AllKeys]: DotValue<BaseLocale, K> extends string ? K : never;
}[AllKeys];

/** Keys that map to functions */
type DynamicKey = {
  [K in AllKeys]: DotValue<BaseLocale, K> extends (...args: never[]) => string ? K : never;
}[AllKeys];

/** Strongly typed translation function */
export type TFunction = {
  // function keys → require params
  <K extends DynamicKey>(
    key: K,
    ...args: DotValue<BaseLocale, K> extends (...args: infer A) => string ? A : never
  ): string;

  // string keys → no params
  (key: StaticKey): string;
};

/** Resolve a value from an object using a dot-path string */
export const resolve = (obj: Record<string, unknown>, path: string): unknown => {
  let current: unknown = obj;

  for (const segment of path.split(".")) {
    if (current == null || typeof current !== "object") {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }

  return current;
};
